/**
 * HelperLines - Figma-style alignment guides rendered inside the React Flow pane.
 *
 * Given guide coordinates in FLOW coordinates, this draws thin (1px) lines that
 * span the viewport at the matching screen positions. It reads the current
 * viewport transform [x, y, zoom] from the React Flow store so the guides track
 * panning and zooming.
 */
import { useStore } from '@xyflow/react'

export interface HelperLinesProps {
  /** Flow-coordinate Y position of the horizontal guide, if any. */
  horizontal?: number
  /** Flow-coordinate X position of the vertical guide, if any. */
  vertical?: number
}

const ACCENT = '#3b82f6'

export default function HelperLines({ horizontal, vertical }: HelperLinesProps) {
  const transform = useStore((s) => s.transform)

  if (horizontal === undefined && vertical === undefined) {
    return null
  }

  const [tx, ty, zoom] = transform

  // Convert flow coords -> screen coords: screen = flow * zoom + translate.
  const verticalScreenX = vertical !== undefined ? vertical * zoom + tx : undefined
  const horizontalScreenY = horizontal !== undefined ? horizontal * zoom + ty : undefined

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {verticalScreenX !== undefined && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            transform: `translateX(${verticalScreenX}px)`,
            backgroundColor: ACCENT,
          }}
        />
      )}
      {horizontalScreenY !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: 1,
            transform: `translateY(${horizontalScreenY}px)`,
            backgroundColor: ACCENT,
          }}
        />
      )}
    </div>
  )
}
